import type { ReactNode } from "react";

interface FilterPanelProps {
  genres: string[];
  regions: string[];
  years: number[];
  selectedGenres: string[];
  selectedRegions: string[];
  selectedYears: number[];
  onToggleGenre: (value: string) => void;
  onToggleRegion: (value: string) => void;
  onToggleYear: (value: number) => void;
  onClearGenres: () => void;
  onReset: () => void;
}

function ChipButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
        active
          ? "bg-primary-500 font-medium text-background-50"
          : "bg-background-100 text-foreground-700 hover:bg-background-200 hover:text-foreground-900"
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterPanel({
  genres,
  regions,
  years,
  selectedGenres,
  selectedRegions,
  selectedYears,
  onToggleGenre,
  onToggleRegion,
  onToggleYear,
  onClearGenres,
  onReset,
}: FilterPanelProps) {
  const rows: { label: string; content: ReactNode }[] = [
    {
      label: "类型",
      content: (
        <div className="flex flex-wrap gap-2">
          <ChipButton active={selectedGenres.length === 0} onClick={onClearGenres}>
            全部
          </ChipButton>
          {genres.map((genre) => (
            <ChipButton
              key={genre}
              active={selectedGenres.includes(genre)}
              onClick={() => onToggleGenre(genre)}
            >
              {genre}
            </ChipButton>
          ))}
        </div>
      ),
    },
    {
      label: "地区",
      content: (
        <div className="flex flex-wrap gap-2">
          {regions.map((region) => (
            <ChipButton
              key={region}
              active={selectedRegions.includes(region)}
              onClick={() => onToggleRegion(region)}
            >
              {region}
            </ChipButton>
          ))}
        </div>
      ),
    },
    {
      label: "年份",
      content: (
        <div className="flex flex-wrap gap-2">
          {years.map((year) => (
            <ChipButton
              key={year}
              active={selectedYears.includes(year)}
              onClick={() => onToggleYear(year)}
            >
              {year}
            </ChipButton>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex flex-col gap-4">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-col gap-2 sm:flex-row sm:gap-4">
            <span className="shrink-0 pt-1.5 text-xs font-medium text-foreground-500 sm:w-12">
              {row.label}
            </span>
            <div className="min-w-0 flex-1">{row.content}</div>
          </div>
        ))}
      </div>
      {(selectedGenres.length > 0 || selectedRegions.length > 0 || selectedYears.length > 0) && (
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
          <span className="text-xs text-foreground-500">
            已选择 {selectedGenres.length + selectedRegions.length + selectedYears.length} 个条件
          </span>
          <button
            type="button"
            onClick={onReset}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-primary-600 hover:text-primary-700"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-refresh-line text-sm" />
            </span>
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}